import { useCallback, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import { get, set, isEmpty, compact } from "lodash";
import { TFetcher } from "../type";
import { useLoadState } from "../component/hook";

export type Option = {
	value: any;
	label: string | JSX.Element;
	[key: string]: any;
};

export default function SelectSearch({
	id = "first",
	isMulti = false,
	onChange,
	onCreateOption,
	fetcher,
	defaultValue,
	filterOption
}: {
	id?: string;
	isMulti?: boolean;
	onChange: (option: Option | Option[], actionMeta?: any) => any;
	onCreateOption: (inputValue: any) => Option | Promise<Option>;
	fetcher: (args: {
		setOptions: Function;
		setValue: Function;
	}) => Promise<Option[]>;
	defaultValue?: any;
	filterOption?: (option: Option, input: string) => boolean;
}) {
	const [options, setOptions] = useState<Option[]>([]);
	useLoadState(
		useCallback(() => {
			return fetcher({ setOptions, setValue });
		}, [fetcher]),
		setOptions
	);

	const [value, setValue] = useState<Option | Option[]>();
	useLoadState(
		useCallback(() => {
			if (!defaultValue || get(window, `selectSearch.${id}.loaded`)) {
				return;
			}
			let defaults = findOptionByValue(defaultValue, options);
			if (!isEmpty(defaults)) {
				set(window, `selectSearch.${id}.loaded`, true);
				return defaults;
			}
		}, [id, defaultValue, options]),
		setValue
	);

	const [isLoading, setIsLoading] = useState(false);

	return (
		<CreatableSelect
			isMulti={isMulti}
			isClearable
			isDisabled={isLoading}
			isLoading={isLoading}
			onChange={async (option: any, actionMeta?: any) => {
				try {
					await onChange(option, actionMeta);
					setValue(option);
				} catch (e: any) {
					const message = e?.response?.data || e.toString();
					toast.error(message);
				}
			}}
			onCreateOption={async (inputValue: any) => {
				try {
					setIsLoading(true);
					const newOption = await onCreateOption(inputValue);
					setOptions([...options, newOption]);
					setValue((currentValue) =>
						isMulti ? [...(currentValue as Option[]), newOption] : newOption
					);
				} catch (e: any) {
					const message = e?.response?.data || e.toString();
					toast.error(`onCreateOption | ${message}`);
				} finally {
					setIsLoading(false);
				}
			}}
			options={options}
			value={value}
			filterOption={filterOption}
		/>
	);
}

export const NonCreatableSelect = ({
	isMulti = false,
	onChange,
	fetcher,
	defaultValue,
	filterOption
}: {
	isMulti?: boolean;
	onChange: (option: Option | Option[], actionMeta?: any) => any;
	fetcher: TFetcher<Option[]>;
	defaultValue?: any;
	filterOption?: (option: Option, input: string) => boolean;
}) => {
	const [options, setOptions] = useState<Option[]>([]);
	useLoadState(fetcher, setOptions);

	const [value, setValue] = useState<Option | Option[]>();
	useLoadState(
		useCallback(() => {
			const defaults = findOptionByValue(defaultValue, options);
			return defaults;
		}, [defaultValue, options]),
		setValue
	);

	return (
		<Select
			isMulti={isMulti}
			isClearable
			onChange={async (option: any, actionMeta?: any) => {
				try {
					await onChange(option, actionMeta);
					setValue(option);
				} catch (e: any) {
					const message = e?.response?.data || e.toString();
					toast.error(message);
				}
			}}
			options={options}
			value={value}
			filterOption={filterOption}
		/>
	);
};

export const createOption = (label: string) => ({
	label,
	value: label.toLowerCase().replace(/\W/g, "")
});

function findOptionByValue(
	value: string | string[],
	options: Option[]
): Option | Option[] | undefined {
	if (typeof value === "string") {
		return findOption(value, options);
	}

	if (Array.isArray(value)) {
		const result = [];
		for (let each of value) {
			const option = findOption(each, options);
			result.push(option);
		}
		return compact(result);
	}
}

function findOption(value: string, options: Option[]): Option {
	let option: Option;
	for (let o of options) {
		if (value === o.value) {
			option = o;
			break;
		}
	}

	return option!;
}
